import React, { useEffect, useState } from "react";
import { api, ApiError, type Subscription, type PgPlans, type PgSlab } from "../../api";
import { useToast } from "../../ToastContext";
import { useAuth } from "../../AuthContext";
import { useLanguage } from "../../i18n";
import { loadRazorpayCheckout } from "../../razorpayCheckout";
import { PlanChangedModal } from "../shared/PlanChangedModal";

type PlanId = "basic" | "pro" | "premium";
type Cycle = "monthly" | "yearly";

const PRICES: Record<PlanId, { monthly: string; yearly: string }> = {
  basic: { monthly: "₹0", yearly: "₹0" },
  pro: { monthly: "₹99", yearly: "₹999" },
  premium: { monthly: "₹199", yearly: "₹1,999" },
};

// One row per capability — a string value is shown literally (e.g. a
// listing count), a boolean renders as a tick/dash. Matches OWNER_LIMITS in
// the server's plans.ts exactly, so this table never claims a plan includes
// something the backend doesn't actually gate that way.
const FEATURE_ROWS: { key: string; basic: string | boolean; pro: string | boolean; premium: string | boolean }[] = [
  { key: "rowActiveListings", basic: "1", pro: "3", premium: "unlimited" },
  { key: "rowEnquiries", basic: true, pro: true, premium: true },
  { key: "rowLedger", basic: true, pro: true, premium: true },
  { key: "rowMarketplace", basic: false, pro: true, premium: true },
  { key: "rowFeatured", basic: false, pro: true, premium: true },
  { key: "rowAutoReminders", basic: false, pro: true, premium: true },
  { key: "rowTopListing", basic: false, pro: false, premium: true },
  { key: "rowFreeEstamp", basic: false, pro: false, premium: true },
  { key: "rowAnalytics", basic: false, pro: false, premium: true },
];

function cmpCell(v: string | boolean) {
  if (typeof v === "string") return v;
  return v ? <span className="cmp-tick">✓</span> : <span className="cmp-dash">–</span>;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export function OwnerPlans({ onBack }: { onBack: () => void }) {
  const { t } = useLanguage();
  const toast = useToast();
  const { user } = useAuth();
  const [sub, setSub] = useState<Subscription | null>(null);
  const [listingCount, setListingCount] = useState(0);
  const [busyPlan, setBusyPlan] = useState<PlanId | null>(null);
  const [cycle, setCycle] = useState<Cycle>("monthly");
  const [razorpayKeyId, setRazorpayKeyId] = useState<string | null>(null);
  const [justPaidPlan, setJustPaidPlan] = useState<PlanId | null>(null);
  const [pgPlans, setPgPlans] = useState<PgPlans | null>(null);
  const [pgTier, setPgTier] = useState<"pro" | "premium">("pro");
  const [busySlab, setBusySlab] = useState<PgSlab | null>(null);

  function load() {
    api.get("/api/subscription").then(setSub).catch(() => {});
    api.get("/api/properties/mine").then((d) => setListingCount(d.properties.length)).catch(() => {});
  }
  useEffect(() => {
    load();
    api.get("/api/config").then(({ razorpayKeyId }) => setRazorpayKeyId(razorpayKeyId)).catch(() => {});
    api.get("/api/subscription/pg-plans").then(setPgPlans).catch(() => {});
  }, []);

  // Waits a few seconds for the webhook to land after checkout succeeds,
  // rather than showing a stale plan right after paying — same pattern as
  // TenantPayments' rent checkout.
  async function waitForPlanChange(plan: PlanId) {
    for (let i = 0; i < 6; i++) {
      const fresh: Subscription = await api.get("/api/subscription");
      if (fresh.plan === plan) {
        setSub(fresh);
        setJustPaidPlan(plan);
        return;
      }
      await new Promise((r) => setTimeout(r, 1500));
    }
    load();
  }

  // PG owners buy a bed slab instead of the flat Pro/Premium. Same Razorpay
  // subscription checkout as the flat plans — the slab just picks which
  // pre-created Plan (and therefore price) the mandate bills against.
  async function chooseSlab(slab: PgSlab) {
    setBusySlab(slab);
    try {
      await loadRazorpayCheckout();
      const order = await api.post("/api/subscription/checkout", { plan: pgTier, cycle, pgSlab: slab });
      await new Promise<void>((resolve, reject) => {
        const rzp = new window.Razorpay({
          key: order.keyId,
          subscription_id: order.subscriptionId,
          name: "ToLetBook",
          image: "https://toletbook.in/icons/icon-512.png",
          description: `${t("ownerPlans.pgTitle")} — ${t(`ownerPlans.${SLAB_NAME_KEY[slab]}`)}`,
          handler: () => resolve(),
          modal: { ondismiss: () => reject(new Error("cancelled")) },
        });
        rzp.on("payment.failed", () => reject(new Error("failed")));
        rzp.open();
      });
      for (let i = 0; i < 6; i++) {
        const fresh: Subscription = await api.get("/api/subscription");
        if (fresh.pg?.slab === slab && fresh.plan === pgTier) {
          setSub(fresh);
          setJustPaidPlan(pgTier);
          return;
        }
        await new Promise((r) => setTimeout(r, 1500));
      }
      load();
    } catch (err) {
      if (err instanceof Error && err.message === "failed") toast(t("ownerPlans.switchFailedToast"));
      else if (err instanceof ApiError) toast(err.message);
    } finally {
      setBusySlab(null);
    }
  }

  async function pauseNow() {
    setBusyPlan(currentPlan);
    try {
      await api.post("/api/subscription/pause");
      load();
      toast(t("ownerPlans.pausedToast"));
    } catch (err) {
      if (err instanceof ApiError) toast(err.message);
    } finally {
      setBusyPlan(null);
    }
  }

  async function resumeNow() {
    setBusyPlan(currentPlan);
    try {
      await api.post("/api/subscription/resume");
      load();
      toast(t("ownerPlans.resumedToast"));
    } catch (err) {
      if (err instanceof ApiError) toast(err.message);
    } finally {
      setBusyPlan(null);
    }
  }

  async function switchTo(plan: PlanId) {
    setBusyPlan(plan);
    try {
      if (plan === "basic" || !razorpayKeyId) {
        await api.post("/api/subscription", { plan });
        load();
        toast(t("ownerPlans.switchedToast", { plan: t(`ownerPlans.${plan}Name`) }));
        return;
      }
      await loadRazorpayCheckout();
      // Owner Pro/Premium are real recurring subscriptions — Razorpay's
      // checkout takes a subscription_id (not order_id) and derives the
      // amount from the Plan itself, so amount/currency aren't passed.
      const order = await api.post("/api/subscription/checkout", { plan, cycle });
      await new Promise<void>((resolve, reject) => {
        const rzp = new window.Razorpay({
          key: order.keyId,
          subscription_id: order.subscriptionId,
          name: "ToLetBook",
          image: "https://toletbook.in/icons/icon-512.png",
          description: `${t(`ownerPlans.${plan}Name`)} — ${cycle === "yearly" ? t("ownerPlans.billingYearly") : t("ownerPlans.billingMonthly")}`,
          handler: () => resolve(),
          modal: { ondismiss: () => reject(new Error("cancelled")) },
        });
        rzp.on("payment.failed", () => reject(new Error("failed")));
        rzp.open();
      });
      await waitForPlanChange(plan);
    } catch (err) {
      if (err instanceof Error && err.message === "failed") toast(t("ownerPlans.switchFailedToast"));
      else if (err instanceof ApiError) toast(err.message);
      // "cancelled" (user closed checkout) fails silently
    } finally {
      setBusyPlan(null);
    }
  }

  const currentPlan = (sub?.plan as PlanId) ?? "basic";
  // Anyone who runs PG beds (or signed up as a PG owner) sees bed-based PG
  // plans in place of the flat Pro/Premium comparison below.
  const isPgOwner = !!sub?.pg && (sub.pg.beds > 0 || user?.accountType === "pg" || user?.accountType === "both");
  // Infinity (unlimited, Premium) doesn't survive JSON — it arrives as null.
  const cap = sub?.limits?.listingCap;
  const capLabel = !sub ? "" : cap == null ? t("ownerPlans.featListingUnlimited") : t("ownerPlans.listingsUsed", { used: listingCount, cap: Number(cap) });

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <button className="icon-btn" onClick={onBack} aria-label="Back">‹</button>
        <div className="section-title" style={{ marginBottom: 0 }}>{t("ownerPlans.title")}</div>
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-row">
          <div>
            <div className="sub">{t("ownerPlans.currentPlanLabel")}</div>
            <b>{t(`ownerPlans.${currentPlan}Name`)}</b>
          </div>
          {!isPgOwner && <span className="badge vacant">{capLabel}</span>}
        </div>
        {sub?.status === "trialing" && (
          <div className="sub" style={{ marginTop: 8, color: "var(--brand-700)", fontWeight: 700 }}>
            🎁 {t("ownerPlans.trialBadge")} · {t("ownerPlans.trialEndsOn", { date: fmtDate(sub.trialEndsAt) })}
          </div>
        )}
        {sub?.status === "active" && sub.currentPeriodEnd && (
          <div className="sub" style={{ marginTop: 8 }}>{t("ownerPlans.renewsOn", { date: fmtDate(sub.currentPeriodEnd) })}</div>
        )}
        {sub?.status === "paused" && (
          <div className="sub" style={{ marginTop: 8, color: "var(--ink-faint)", fontWeight: 700 }}>
            ⏸ {t("ownerPlans.pausedBadge")}
          </div>
        )}
        {sub?.status === "paused" ? (
          <button type="button" className="link-btn" style={{ marginTop: 8 }} disabled={busyPlan !== null} onClick={resumeNow}>
            {t("ownerPlans.resumePlanBtn")}
          </button>
        ) : (
          currentPlan !== "basic" && (
            <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
              <button type="button" className="link-btn" disabled={busyPlan !== null} onClick={pauseNow}>
                {t("ownerPlans.pausePlanBtn")}
              </button>
              <button type="button" className="link-btn" disabled={busyPlan !== null} onClick={() => switchTo("basic")}>
                {t("ownerPlans.cancelPlanBtn")}
              </button>
            </div>
          )
        )}
      </div>

      {isPgOwner && sub?.pg && pgPlans && (
        <PgPlansSection
          pg={sub.pg}
          plans={pgPlans}
          tier={pgTier}
          setTier={setPgTier}
          cycle={cycle}
          setCycle={setCycle}
          busySlab={busySlab}
          canPay={!!razorpayKeyId}
          onChoose={chooseSlab}
        />
      )}

      {!isPgOwner && (
      <>
      <div className="seg-control">
        <button className={`seg-btn${cycle === "monthly" ? " active" : ""}`} onClick={() => setCycle("monthly")}>
          {t("ownerPlans.billingMonthly")}
        </button>
        <button className={`seg-btn${cycle === "yearly" ? " active" : ""}`} onClick={() => setCycle("yearly")}>
          {t("ownerPlans.billingYearly")}
        </button>
      </div>

      <div className="cmp-table">
        {/* Header row — 4 cells, direct children of the one shared grid */}
        <div className="cmp-th cmp-label-cell" />
        <div className="cmp-th">
          <div className="cmp-plan-name">{t("ownerPlans.basicName")}</div>
          <div className="cmp-plan-price">{PRICES.basic[cycle]}</div>
        </div>
        <div className="cmp-th cmp-pro-col">
          <span className="cmp-pop-badge">{t("ownerPlans.mostPopular")}</span>
          <div className="cmp-plan-name">{t("ownerPlans.proName")}</div>
          <div className="cmp-plan-price">
            {PRICES.pro[cycle]}
            <small>{cycle === "monthly" ? "/mo" : "/yr"}</small>
          </div>
        </div>
        <div className="cmp-th">
          <div className="cmp-plan-name">{t("ownerPlans.premiumName")}</div>
          <div className="cmp-plan-price">
            {PRICES.premium[cycle]}
            <small>{cycle === "monthly" ? "/mo" : "/yr"}</small>
          </div>
        </div>

        {/* One feature row = 4 more direct children of the same grid */}
        {FEATURE_ROWS.map((row) => (
          <React.Fragment key={row.key}>
            <div className="cmp-td cmp-label-cell">
              <div className="cmp-row-name">{t(`ownerPlans.${row.key}`)}</div>
              <div className="cmp-row-desc">{t(`ownerPlans.${row.key}Desc`)}</div>
            </div>
            <div className="cmp-td">{cmpCell(row.basic)}</div>
            <div className="cmp-td cmp-pro-col">{cmpCell(row.pro)}</div>
            <div className="cmp-td">{row.premium === "unlimited" ? t("ownerPlans.unlimitedLabel") : cmpCell(row.premium)}</div>
          </React.Fragment>
        ))}

        {/* CTA row — same 4-column grid, no borders/background needed */}
        <div className="cmp-cta-cell" />
        <div className="cmp-cta-cell">
          {currentPlan === "basic" ? (
            <button className="btn-outline cmp-mini-btn" disabled style={{ opacity: 0.6, cursor: "default" }}>
              {t("ownerPlans.ctaCurrent")}
            </button>
          ) : (
            <button className="btn-outline cmp-mini-btn" disabled={busyPlan !== null} onClick={() => switchTo("basic")}>
              {t("ownerPlans.ctaSwitch")}
            </button>
          )}
        </div>
        <div className="cmp-cta-cell">
          {currentPlan === "pro" ? (
            <button className="btn-outline cmp-mini-btn" disabled style={{ opacity: 0.6, cursor: "default" }}>
              {t("ownerPlans.ctaCurrent")}
            </button>
          ) : (
            <button className="btn-solid cmp-mini-btn" disabled={busyPlan !== null} onClick={() => switchTo("pro")}>
              {t("ownerPlans.ctaSwitch")}
            </button>
          )}
        </div>
        <div className="cmp-cta-cell">
          {currentPlan === "premium" ? (
            <button className="btn-outline cmp-mini-btn" disabled style={{ opacity: 0.6, cursor: "default" }}>
              {t("ownerPlans.ctaCurrent")}
            </button>
          ) : (
            <button className="btn-outline cmp-mini-btn" disabled={busyPlan !== null} onClick={() => switchTo("premium")}>
              {t("ownerPlans.ctaSwitch")}
            </button>
          )}
        </div>
      </div>

      </>
      )}

      <div className="sub" style={{ textAlign: "center", marginTop: 4 }}>
        {razorpayKeyId ? t("ownerPlans.securePaymentNote") : t("ownerPlans.noChargeNote")}
      </div>

      {justPaidPlan && (
        <PlanChangedModal planName={t(`ownerPlans.${justPaidPlan}Name`)} onClose={() => setJustPaidPlan(null)} />
      )}
    </>
  );
}

const SLAB_NAME_KEY: Record<PgSlab, string> = {
  starter: "pgSlabStarter",
  standard: "pgSlabStandard",
  plus: "pgSlabPlus",
  large: "pgSlabLarge",
};

function fmtRupees(n: number) {
  return "₹" + n.toLocaleString("en-IN");
}

function PgPlansSection({
  pg,
  plans,
  tier,
  setTier,
  cycle,
  setCycle,
  busySlab,
  canPay,
  onChoose,
}: {
  pg: NonNullable<Subscription["pg"]>;
  plans: PgPlans;
  tier: "pro" | "premium";
  setTier: (t: "pro" | "premium") => void;
  cycle: Cycle;
  setCycle: (c: Cycle) => void;
  busySlab: PgSlab | null;
  canPay: boolean;
  onChoose: (s: PgSlab) => void;
}) {
  const { t } = useLanguage();
  const priceFor = (slab: PgPlans["slabs"][number]) =>
    slab.prices[cycle] + (tier === "premium" ? plans.premiumSurcharge[cycle] : 0);

  const usage = pg.grandfathered
    ? t("ownerPlans.pgBedsUnlimited", { used: pg.beds, date: fmtDate(pg.graceUntil) })
    : pg.slab
      ? t("ownerPlans.pgBedsUsed", { used: pg.beds, cap: pg.bedCap ?? "∞" })
      : t("ownerPlans.pgBedsFreeUsed", { used: pg.beds, cap: pg.bedCap ?? plans.freeBeds });

  return (
    <div style={{ marginBottom: 18 }}>
      <div className="section-title" style={{ marginBottom: 4 }}>{t("ownerPlans.pgTitle")}</div>
      <div className="sub" style={{ marginBottom: 10, fontSize: 13 }}>{t("ownerPlans.pgIntro")}</div>
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-row">
          <b>{usage}</b>
        </div>
        {pg.grandfathered && <div className="sub" style={{ marginTop: 6 }}>{t("ownerPlans.pgGraceNote", { date: fmtDate(pg.graceUntil) })}</div>}
        {!pg.slab && !pg.grandfathered && <div className="sub" style={{ marginTop: 6 }}>{t("ownerPlans.pgFreeNote", { n: plans.freeBeds })}</div>}
      </div>

      <div className="seg-control" style={{ marginBottom: 8 }}>
        <button className={`seg-btn${tier === "pro" ? " active" : ""}`} onClick={() => setTier("pro")}>
          {t("ownerPlans.pgTierPro")}
        </button>
        <button className={`seg-btn${tier === "premium" ? " active" : ""}`} onClick={() => setTier("premium")}>
          {t("ownerPlans.pgTierPremium")}
        </button>
      </div>
      {tier === "premium" && <div className="sub" style={{ marginBottom: 8, fontSize: 13 }}>{t("ownerPlans.pgTierPremiumNote")}</div>}
      <div className="seg-control" style={{ marginBottom: 12 }}>
        <button className={`seg-btn${cycle === "monthly" ? " active" : ""}`} onClick={() => setCycle("monthly")}>
          {t("ownerPlans.billingMonthly")}
        </button>
        <button className={`seg-btn${cycle === "yearly" ? " active" : ""}`} onClick={() => setCycle("yearly")}>
          {t("ownerPlans.billingYearly")}
        </button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {plans.slabs.map((slab) => {
          const tooSmall = slab.maxBeds !== null && slab.maxBeds < pg.beds;
          const current = pg.slab === slab.key;
          const fits = pg.recommendedSlab === slab.key;
          const range =
            slab.maxBeds === null
              ? t("ownerPlans.pgBedRangeOpen", { min: slab.minBeds })
              : t("ownerPlans.pgBedRange", { min: slab.minBeds, max: slab.maxBeds });
          return (
            <div key={slab.key} className="card" style={{ marginBottom: 0, borderColor: fits ? "var(--brand-500)" : undefined }}>
              <div className="card-row">
                <div>
                  <b>{t(`ownerPlans.${SLAB_NAME_KEY[slab.key]}`)}</b>
                  <div className="sub">{range}</div>
                  {fits && <div className="sub" style={{ color: "var(--brand-700)", fontWeight: 700 }}>{t("ownerPlans.pgFits", { n: pg.beds })}</div>}
                </div>
                <div style={{ textAlign: "right" }}>
                  <b>{fmtRupees(priceFor(slab))}</b>
                  <small className="sub">{cycle === "monthly" ? "/mo" : "/yr"}</small>
                </div>
              </div>
              <div style={{ marginTop: 10 }}>
                {current ? (
                  <button className="btn-outline btn-sm" disabled style={{ opacity: 0.6, cursor: "default", width: "100%" }}>
                    {t("ownerPlans.ctaCurrent")}
                  </button>
                ) : tooSmall ? (
                  <div className="sub">{t("ownerPlans.pgTooSmall")}</div>
                ) : !canPay ? (
                  <div className="sub">{t("ownerPlans.pgNoPayments")}</div>
                ) : !slab.available ? (
                  <div className="sub">{t("ownerPlans.pgUnavailable")}</div>
                ) : (
                  <button className="btn-solid btn-sm" style={{ width: "100%" }} disabled={busySlab !== null} onClick={() => onChoose(slab.key)}>
                    {t("ownerPlans.pgChoose")}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="sub" style={{ marginTop: 10 }}>{t("ownerPlans.pgIncludes")}</div>
    </div>
  );
}
